import { Button, Select } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import { PostCase, postCaseDisplayName } from "../../../api/postCaseAPI";
import FileUploadInput, { FileUploadMode } from "../../upload/FileUploadInput";

export const UNASSIGNED_VALUE = "__unassigned__";

export const UPLOAD_MODAL_BODY_STYLE: React.CSSProperties = {
  maxHeight: "calc(100vh - 260px)",
  overflowY: "auto",
  overflowX: "hidden",
};

export type UploadCaseValue = number | typeof UNASSIGNED_VALUE;

export interface CaseFileGroup {
  key: string;
  caseValue: UploadCaseValue;
  fileList: any[];
}

let groupSeq = 0;

export const createCaseFileGroup = (caseValue: UploadCaseValue = UNASSIGNED_VALUE): CaseFileGroup => ({
  key: `case-group-${++groupSeq}`,
  caseValue,
  fileList: [],
});

export const resolveUploadCaseId = (value: UploadCaseValue): number | null =>
  value !== UNASSIGNED_VALUE ? Number(value) : null;

export const flattenCaseFileGroups = (groups: CaseFileGroup[]) =>
  groups.flatMap((g) =>
    g.fileList.map((item) => ({ item, postCaseId: resolveUploadCaseId(g.caseValue) }))
  );

export const findDuplicateAcrossGroups = (groups: CaseFileGroup[]): string | null => {
  const seen = new Map<string, string>();
  for (const g of groups) {
    for (const item of g.fileList) {
      const f = item.originFileObj || item;
      const id = `${f.name}__${f.size}`;
      const owner = seen.get(id);
      if (owner && owner !== g.key) return f.name;
      seen.set(id, g.key);
    }
  }
  return null;
};

const caseOptions = (cases: PostCase[]) => [
  { label: "미배정", value: UNASSIGNED_VALUE },
  ...cases.map((pc, idx) => ({
    label: pc.organ
      ? `${postCaseDisplayName(pc, idx)} — ${pc.organ}`
      : postCaseDisplayName(pc, idx),
    value: pc.postCaseId,
  })),
];

interface Props {
  cases: PostCase[];
  groups: CaseFileGroup[];
  setGroups: React.Dispatch<React.SetStateAction<CaseFileGroup[]>>;
  mode: FileUploadMode;
  enableScroll?: boolean;
  /** 모드 기본값 대신 허용할 확장자 (예: 동영상) */
  allowedExtensions?: string[];
  renderFiles?: (group: CaseFileGroup) => React.ReactNode;
}

const CaseFileGroups = ({
  cases,
  groups,
  setGroups,
  mode,
  enableScroll,
  allowedExtensions,
  renderFiles,
}: Props) => {
  const hasCases = cases.length > 0;

  const updateGroup = (key: string, patch: Partial<CaseFileGroup>) =>
    setGroups((prev) => prev.map((g) => (g.key === key ? { ...g, ...patch } : g)));

  const removeGroup = (key: string) =>
    setGroups((prev) => prev.filter((g) => g.key !== key));

  const addGroup = () =>
    setGroups((prev) => {
      const used = new Set(prev.map((g) => g.caseValue));
      const next = cases.find((pc) => !used.has(pc.postCaseId));
      return [...prev, createCaseFileGroup(next ? next.postCaseId : UNASSIGNED_VALUE)];
    });

  const usedCount = (value: UploadCaseValue) =>
    groups.filter((g) => g.caseValue === value).length;

  return (
    <div>
      {groups.map((group) => (
        <div
          key={group.key}
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
          }}
        >
          {hasCases && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontWeight: 500, flex: "0 0 auto" }}>Case</span>
              <Select
                style={{ flex: 1 }}
                value={group.caseValue}
                onChange={(v) => updateGroup(group.key, { caseValue: v })}
                options={caseOptions(cases)}
                status={usedCount(group.caseValue) > 1 ? "warning" : undefined}
              />
              {groups.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeGroup(group.key)}
                />
              )}
            </div>
          )}
          <FileUploadInput
            mode={mode}
            multiple={true}
            showSuccessMessage={false}
            enableScroll={enableScroll}
            allowedExtensions={allowedExtensions}
            value={{ fileList: group.fileList }}
            onChange={(val) => updateGroup(group.key, { fileList: val?.fileList ?? [] })}
          />
          {renderFiles?.(group)}
        </div>
      ))}
      {hasCases && (
        <Button type="dashed" block icon={<PlusOutlined />} onClick={addGroup}>
          Case 추가
        </Button>
      )}
    </div>
  );
};

export default CaseFileGroups;
